import {
  ADMIN_SEED_ANCHOR_MS,
  SEED_AI_MODELS,
  SHIPPED_SYSTEM_PROMPT_BASE,
  SYSTEM_PROMPT_BODY_MAX_BYTES,
  SYSTEM_PROMPT_KITS,
  type SystemPromptKit,
  type SystemPromptVersionMeta,
} from '@/versions/v1/shared/admin-seed';
import { SEED_APP_MODELS, getModel, type AppModel } from './AiModelsScene.transform';

import { formatAdminDateTime } from './admin-format';

export { SYSTEM_PROMPT_BODY_MAX_BYTES, SYSTEM_PROMPT_KITS, SHIPPED_SYSTEM_PROMPT_BASE };

export const KIT_LABELS: Record<SystemPromptKit, string> = {
  claude: 'Anthropic',
  google: 'Google models',
};

export type SystemPromptKitState = {
  liveVersion: number | null;
  live: { meta: SystemPromptVersionMeta; body: string } | null;
  draft: { meta: SystemPromptVersionMeta; body: string } | null;
  versions: SystemPromptVersionMeta[];
  usesShippedDefault: boolean;
};

export type ScopeEntry = {
  modelId: string | null;
  name: string | null;
  state: SystemPromptKitState;
};

export type MapRow = {
  key: string;
  modelId: string | null;
  modelLabel: string;
  scope: ScopeEntry | null;
  usesDefault: boolean;
  isDisabled: boolean;
};

export type ModalState =
  | { kind: 'create'; forKit: SystemPromptKit; presetModelId?: string }
  | { kind: 'edit'; kit: SystemPromptKit; scope: ScopeEntry }
  | {
      kind: 'duplicate';
      sourceScope: ScopeEntry;
      sourceKit: SystemPromptKit;
      sourceBody: string;
    };

const TEMPLATE_PATTERN = /\{+[^{}]*}+/g;
const IDENTIFIER = /^[a-zA-Z_][a-zA-Z0-9_]*$/;
const STATE_PREFIXES = ['app:', 'user:', 'temp:'] as const;

function isAdkTemplateKey(key: string): boolean {
  const parts = key.split(':');
  if (parts.length === 0 || parts.length > 2) return false;
  if (parts.length === 1) return IDENTIFIER.test(key);
  const prefix = `${parts[0]}:`;
  return (
    (STATE_PREFIXES as readonly string[]).includes(prefix) &&
    IDENTIFIER.test(parts[1] ?? '')
  );
}

export function findAdkTemplateKeys(template: string): string[] {
  const keys = new Set<string>();
  for (const match of template.matchAll(TEMPLATE_PATTERN)) {
    let key = match[0].replace(/^\{+/, '').replace(/\}+$/, '').trim();
    const isOptional = key.endsWith('?');
    if (isOptional) key = key.slice(0, -1);
    if (isAdkTemplateKey(key)) keys.add(`${key}${isOptional ? '?' : ''}`);
  }
  return [...keys];
}

export function bodyBytes(value: string): number {
  return new TextEncoder().encode(value).length;
}

export function formatPromptDate(epochMs: number): string {
  return formatAdminDateTime(epochMs);
}

/** Deterministic mock clock for local prompt mutations (no Date.now). */
export function mockPromptVersionClock(version: number): number {
  return ADMIN_SEED_ANCHOR_MS + version * 3_600_000;
}

export function mockPromptVersionId(version: number): string {
  return `spv-mock-v${version}`;
}

export function scopeDisplayName(
  kit: SystemPromptKit,
  modelId: string | null,
  name: string | null,
): string {
  if (modelId === null) return `Default — ${KIT_LABELS[kit]}`;
  return name ?? modelId;
}

export function sdkShortLabel(kit: SystemPromptKit): string {
  return kit === 'claude' ? 'Anthropic' : 'Google';
}

export function statusTone(
  status: SystemPromptVersionMeta['status'],
): 'default' | 'danger' {
  return status === 'draft' ? 'danger' : 'default';
}

export function enabledModelsForKit(kit: SystemPromptKit): AppModel[] {
  const kitSeed = kit === 'claude' ? SEED_AI_MODELS.claude : SEED_AI_MODELS.google;
  if (!kitSeed.kitEnabled) return [];
  const catalog = SEED_APP_MODELS.filter((model) => model.kit === kit);
  if (kitSeed.allowedIds === null) return catalog;
  return catalog.filter((model) => kitSeed.allowedIds!.includes(model.id));
}

export function kitModelLabelMap(kit: SystemPromptKit): Map<string, string> {
  const map = new Map<string, string>();
  for (const model of enabledModelsForKit(kit)) {
    map.set(model.id, model.label);
  }
  return map;
}

export function buildScopeState(
  versions: SystemPromptVersionMeta[],
  kit: SystemPromptKit,
  modelId: string | null,
): SystemPromptKitState {
  const scoped = versions
    .filter((version) => version.kit === kit && version.modelId === modelId)
    .sort((a, b) => b.version - a.version);

  const draftMeta = scoped.find((version) => version.status === 'draft') ?? null;
  const liveMeta =
    scoped.find((version) => version.status === 'published') ?? null;

  return {
    liveVersion: liveMeta?.version ?? null,
    live: liveMeta ? { meta: liveMeta, body: liveMeta.body } : null,
    draft: draftMeta ? { meta: draftMeta, body: draftMeta.body } : null,
    versions: scoped,
    usesShippedDefault: !liveMeta && !draftMeta,
  };
}

export function buildScopesFromVersions(
  versions: SystemPromptVersionMeta[],
  kit: SystemPromptKit,
): ScopeEntry[] {
  const modelIds = new Set<string | null>();
  for (const version of versions) {
    if (version.kit === kit) modelIds.add(version.modelId);
  }

  return [...modelIds].map((modelId) => {
    const scopedVersions = versions.filter(
      (version) => version.kit === kit && version.modelId === modelId,
    );
    const named = scopedVersions.find((version) => version.name)?.name ?? null;
    return {
      modelId,
      name: named,
      state: buildScopeState(versions, kit, modelId),
    };
  });
}

export function composeMapRows(
  kit: SystemPromptKit,
  scopes: ScopeEntry[],
  enabledModelIds: ReadonlySet<string>,
  kitModelLabels: Map<string, string>,
): MapRow[] {
  const scopeByModelId = new Map<string | null, ScopeEntry>();
  for (const scope of scopes) {
    scopeByModelId.set(scope.modelId, scope);
  }

  const rows: MapRow[] = [];

  const defaultScope = scopeByModelId.get(null) ?? null;
  rows.push({
    key: '__default__',
    modelId: null,
    modelLabel: `All ${KIT_LABELS[kit]} models`,
    scope: defaultScope,
    usesDefault: false,
    isDisabled: false,
  });

  for (const [modelId, label] of kitModelLabels) {
    const scope = scopeByModelId.get(modelId) ?? null;
    rows.push({
      key: modelId,
      modelId,
      modelLabel: label,
      scope,
      usesDefault: scope === null,
      isDisabled: false,
    });
  }

  for (const scope of scopes) {
    if (scope.modelId !== null && !enabledModelIds.has(scope.modelId)) {
      if (kitModelLabels.has(scope.modelId)) continue;
      rows.push({
        key: `disabled:${scope.modelId}`,
        modelId: scope.modelId,
        modelLabel: getModel(scope.modelId)?.label ?? scope.modelId,
        scope,
        usesDefault: false,
        isDisabled: true,
      });
    }
  }

  return rows;
}

export function promptLabel(kit: SystemPromptKit, row: MapRow): string {
  if (row.usesDefault) return '(default)';
  if (!row.scope) return '(default)';
  return scopeDisplayName(kit, row.scope.modelId, row.scope.name);
}

export function overrideCount(scopes: ScopeEntry[]): number {
  return scopes.filter((scope) => scope.modelId !== null).length;
}

export function availableForNewCount(
  scopes: ScopeEntry[],
  kitModelLabels: Map<string, string>,
): number {
  const scoped = new Set(
    scopes
      .filter((scope) => scope.modelId !== null)
      .map((scope) => scope.modelId as string),
  );
  let count = 0;
  for (const id of kitModelLabels.keys()) {
    if (!scoped.has(id)) count += 1;
  }
  return count;
}

export function nextPromptVersion(
  versions: SystemPromptVersionMeta[],
  kit: SystemPromptKit,
  modelId: string | null,
): number {
  const scoped = versions
    .filter((version) => version.kit === kit && version.modelId === modelId)
    .sort((a, b) => b.version - a.version);
  if (scoped.length === 0) return 1;
  return Math.max(...scoped.map((version) => version.version)) + 1;
}

export function upsertDraftVersion(
  versions: SystemPromptVersionMeta[],
  kit: SystemPromptKit,
  modelId: string | null,
  body: string,
  name: string | null,
): SystemPromptVersionMeta[] {
  const state = buildScopeState(versions, kit, modelId);
  if (state.draft) {
    return versions.map((version) =>
      version.id === state.draft!.meta.id
        ? { ...version, body, name: name ?? version.name }
        : version,
    );
  }

  const version = nextPromptVersion(versions, kit, modelId);
  const created: SystemPromptVersionMeta = {
    id: mockPromptVersionId(version),
    kit,
    modelId,
    name,
    version,
    status: 'draft',
    body,
    createdAt: mockPromptVersionClock(version),
    publishedAt: null,
    createdByEmail: 'alex@example.com',
  };
  return [...versions, created];
}

export function publishDraftVersion(
  versions: SystemPromptVersionMeta[],
  kit: SystemPromptKit,
  modelId: string | null,
  body: string,
  name: string | null,
): SystemPromptVersionMeta[] {
  const state = buildScopeState(versions, kit, modelId);
  const draftId = state.draft?.meta.id;
  const liveId = state.live?.meta.id;

  if (draftId) {
    return versions.map((version) => {
      if (version.id === draftId) {
        return {
          ...version,
          status: 'published' as const,
          publishedAt: mockPromptVersionClock(version.version),
          body,
          name: name ?? version.name,
        };
      }
      if (liveId && version.id === liveId) {
        return { ...version, status: 'archived' as const };
      }
      return version;
    });
  }

  const version = nextPromptVersion(versions, kit, modelId);
  const created: SystemPromptVersionMeta = {
    id: mockPromptVersionId(version),
    kit,
    modelId,
    name,
    version,
    status: 'published',
    body,
    createdAt: mockPromptVersionClock(version),
    publishedAt: mockPromptVersionClock(version),
    createdByEmail: 'alex@example.com',
  };
  return [
    ...versions.map((version) =>
      liveId && version.id === liveId
        ? { ...version, status: 'archived' as const }
        : version,
    ),
    created,
  ];
}

export function discardDraftVersion(
  versions: SystemPromptVersionMeta[],
  kit: SystemPromptKit,
  modelId: string | null,
): SystemPromptVersionMeta[] {
  const state = buildScopeState(versions, kit, modelId);
  const draftId = state.draft?.meta.id;
  if (!draftId) return versions;
  return versions.filter((version) => version.id !== draftId);
}

export function rollbackToVersion(
  versions: SystemPromptVersionMeta[],
  kit: SystemPromptKit,
  modelId: string | null,
  target: SystemPromptVersionMeta,
): SystemPromptVersionMeta[] {
  const state = buildScopeState(versions, kit, modelId);
  const liveId = state.live?.meta.id;

  return versions.map((version) => {
    if (liveId && version.id === liveId) {
      return { ...version, status: 'archived' as const };
    }
    if (version.id === target.id) {
      return {
        ...version,
        status: 'published' as const,
        publishedAt: mockPromptVersionClock(target.version),
      };
    }
    return version;
  });
}

export function findScope(
  scopes: ScopeEntry[],
  modelId: string | null,
): ScopeEntry | undefined {
  return scopes.find((scope) => scope.modelId === modelId);
}

export function refreshScopesForKit(
  versions: SystemPromptVersionMeta[],
  kit: SystemPromptKit,
): ScopeEntry[] {
  return buildScopesFromVersions(versions, kit);
}
